import { useState } from 'react';

import { formatMoney, formatRate } from '@/shared/lib/format';
import { Alert } from '@/shared/ui/Alert';
import { Badge } from '@/shared/ui/Badge';
import { Card, CardBody, CardHeader } from '@/shared/ui/Card';

import {
  camposAlterados,
  colunasDaMatriz,
  EXPLICACAO_FINAL_E_CALCULADO,
  rotuloDoResultado,
  tomDoResultado,
} from './rotulos';
import type { EngineResult, RuleComparison, Simulation } from './types';

function Resultado({ titulo, valor }: { titulo: string; valor: string }) {
  return (
    <div className="rounded-md border border-line p-4">
      <p className="text-xs font-semibold text-ink-500">{titulo}</p>
      <p className="mt-1">
        <Badge tone={tomDoResultado(valor)}>{rotuloDoResultado(valor)}</Badge>
      </p>
    </div>
  );
}

function versaoDescrita(simulation: Simulation): string {
  const v = simulation.parameter_set;
  if (v.source === 'valores_documentados') return 'Valores documentados (sem versao)';
  const base = `Versao ${v.version ?? ''} (${v.status ?? ''})`;
  return v.source === 'alteracoes'
    ? `${base} com alteracoes: ${v.changed_parameters.join(', ')}`
    : base;
}

function Saidas({ result }: { result: EngineResult }) {
  const o = result.outputs;
  const limites = Object.entries(o.limite_automatico ?? {});
  const taxas = Object.entries(o.taxa_referencia_bps ?? {});
  const linhas: [string, string][] = [
    ['Porte', o.porte ?? 'Nao calculado'],
    ['Classe', o.classe ?? 'Nao calculada'],
    [
      'Pontos de restritivos',
      o.pontos_restritivos === null ? 'Nao calculados' : String(o.pontos_restritivos),
    ],
    ['Grupos elegiveis', o.grupos_elegiveis.join(', ') || 'Nenhum'],
    [
      'Limite automatico',
      limites.map(([grupo, valor]) => `${grupo}: ${formatMoney(valor)}`).join('; ') ||
        'Nao calculado',
    ],
    [
      'Taxa de referencia',
      taxas.map(([grupo, bps]) => `${grupo}: ${formatRate(bps)}`).join('; ') ||
        'Nao calculada',
    ],
    ['Modalidades bloqueadas', o.modalidades_bloqueadas.join(', ') || 'Nenhuma'],
    ['Motivos', o.motivos.join(', ') || 'Nenhum'],
    [
      'Consulta Master',
      o.consulta_master?.exigida
        ? `Exigida${o.consulta_master.motivos?.length ? `: ${o.consulta_master.motivos.join('; ')}` : ''}`
        : 'Nao exigida',
    ],
    [
      'Limite e taxa visiveis ao corretor',
      o.limite_e_taxa_visiveis_ao_corretor ? 'Sim' : 'Nao',
    ],
    ['Acumulo do grupo verificado', o.acumulo_grupo_verificado ? 'Sim' : 'Nao'],
  ];
  return (
    <dl className="grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
      {linhas.map(([rotulo, valor]) => (
        <div key={rotulo} className="flex flex-col">
          <dt className="text-xs font-semibold text-ink-500">{rotulo}</dt>
          <dd className="text-ink-900">{valor}</dd>
        </div>
      ))}
    </dl>
  );
}

/** Resultado de uma simulacao: final e calculado, regras, pendencias e saidas. */
export function ResultadoDaSimulacao({ simulation }: { simulation: Simulation }) {
  const [soMudancas, setSoMudancas] = useState(false);
  const result = simulation.result;
  const comparacao = simulation.comparison;
  const porRegra = new Map<string, RuleComparison>(
    (comparacao?.rules ?? []).map((r) => [r.rule, r]),
  );
  const regras = result.rules.filter(
    (r) => !soMudancas || porRegra.get(r.rule)?.changed,
  );

  return (
    <div className="flex flex-col gap-4">
      <Alert tone="warning" title="Simulacao">
        {simulation.notice}
      </Alert>

      <Card>
        <CardHeader
          title="Resultado"
          description={`${versaoDescrita(simulation)}. ${
            simulation.simulate_as_homologated
              ? 'Simulada como homologada: todas as regras decidem.'
              : 'So decidem as regras homologadas da versao.'
          }`}
        />
        <CardBody className="flex flex-col gap-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <Resultado
              titulo="Resultado final (o que valeria)"
              valor={result.final_result}
            />
            <Resultado
              titulo="Resultado calculado (se todas as regras decidissem)"
              valor={result.calculated_result}
            />
          </div>
          <p className="text-xs leading-relaxed text-ink-600">
            {EXPLICACAO_FINAL_E_CALCULADO}
          </p>
          {result.simulated && result.notice ? (
            <Alert tone="info" title="Consulta de provedor ficticio">
              {result.notice}
            </Alert>
          ) : null}
        </CardBody>
      </Card>

      <Card>
        <CardHeader
          title="Comparacao com a versao ativa"
          description="Os mesmos dados ficticios avaliados pela versao ativa, com a mesma forma de homologacao."
        />
        <CardBody className="flex flex-col gap-3">
          {comparacao ? (
            <>
              <div className="grid gap-3 sm:grid-cols-2">
                <Resultado
                  titulo={`Final na versao ativa ${comparacao.active.version ?? ''}`}
                  valor={comparacao.active_result.final_result}
                />
                <Resultado
                  titulo={`Calculado na versao ativa ${comparacao.active.version ?? ''}`}
                  valor={comparacao.active_result.calculated_result}
                />
              </div>
              <p className="text-sm text-ink-700">
                {comparacao.changed_rules === 0
                  ? 'Nenhuma regra mudou em relacao a versao ativa.'
                  : `${comparacao.changed_rules} regra(s) mudaram em relacao a versao ativa.`}{' '}
                {comparacao.changed_parameters.length > 0
                  ? `Parametros diferentes da ativa: ${comparacao.changed_parameters.join(', ')}.`
                  : 'Nenhum parametro difere da ativa.'}{' '}
                {comparacao.changed_outputs.length > 0
                  ? `Saidas que mudaram: ${comparacao.changed_outputs.join(', ')}.`
                  : ''}
              </p>
            </>
          ) : (
            <p className="text-sm text-ink-700">{simulation.comparison_note}</p>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHeader
          title="Regras"
          description="Coluna da matriz, o que a regra concluiu (calculado) e o efeito que vale."
          action={
            comparacao ? (
              <label className="flex items-center gap-2 text-xs text-ink-700">
                <input
                  type="checkbox"
                  checked={soMudancas}
                  onChange={(event) => setSoMudancas(event.target.checked)}
                />
                So o que mudou
              </label>
            ) : null
          }
        />
        <CardBody className="overflow-x-auto p-0">
          <table className="w-full text-left text-xs">
            <thead className="bg-ink-50 text-ink-600">
              <tr>
                <th className="px-3 py-2">Regra</th>
                <th className="px-3 py-2">Coluna</th>
                <th className="px-3 py-2">Calculado</th>
                <th className="px-3 py-2">Efeito</th>
                <th className="px-3 py-2">Na versao ativa</th>
                <th className="px-3 py-2">Mensagem</th>
              </tr>
            </thead>
            <tbody>
              {regras.map((regra) => {
                const cmp = porRegra.get(regra.rule);
                return (
                  <tr
                    key={regra.rule}
                    className={cmp?.changed ? 'bg-warning-50' : 'border-t border-line'}
                  >
                    <td className="px-3 py-2 align-top">
                      <span className="font-semibold">{regra.rule}</span>
                      <span className="block text-ink-500">{regra.name}</span>
                      {regra.homologated ? (
                        <Badge tone="brand">Decide</Badge>
                      ) : regra.shadow ? (
                        <Badge>Sombra</Badge>
                      ) : null}
                    </td>
                    <td
                      className="px-3 py-2 align-top"
                      title={colunasDaMatriz[regra.column]}
                    >
                      {regra.column || '-'}
                    </td>
                    <td className="px-3 py-2 align-top">
                      {rotuloDoResultado(regra.calculated)}
                    </td>
                    <td className="px-3 py-2 align-top">
                      {rotuloDoResultado(regra.effective)}
                      {regra.recommendation ? (
                        <span className="block text-ink-500">
                          Recomendacao: {regra.recommendation}
                        </span>
                      ) : null}
                    </td>
                    <td className="px-3 py-2 align-top">
                      {cmp?.active ? (
                        <>
                          {rotuloDoResultado(cmp.active.calculated)} /{' '}
                          {rotuloDoResultado(cmp.active.effective)}
                          {cmp.changed ? (
                            <span className="block text-warning-700">
                              Mudou:{' '}
                              {cmp.changes
                                .map((c) => camposAlterados[c] ?? c)
                                .join(', ')}
                            </span>
                          ) : null}
                        </>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td className="px-3 py-2 align-top text-ink-700">
                      {regra.effective_note ? (
                        <span className="block text-ink-500">
                          {regra.effective_note}
                        </span>
                      ) : null}
                      {regra.message}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </CardBody>
      </Card>

      <Card>
        <CardHeader
          title="Pendencias"
          description="Todas ficam registradas, mesmo quando outra prevalece."
        />
        <CardBody>
          {result.pendencies.length === 0 ? (
            <p className="text-sm text-ink-700">Nenhuma pendencia.</p>
          ) : (
            <ul className="flex flex-col gap-2 text-sm">
              {result.pendencies.map((p, i) => (
                <li key={`${p.rule}-${i}`}>
                  <span className="font-semibold">{p.rule}</span>:{' '}
                  {rotuloDoResultado(p.outcome)}
                  {p.lane ? ` (esteira ${p.lane})` : ''}. {p.message}
                </li>
              ))}
            </ul>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHeader title="Saidas" />
        <CardBody>
          <Saidas result={result} />
        </CardBody>
      </Card>
    </div>
  );
}
